import type { TokenPriceChangeData } from "@src/features/token-price-changes/lib/coingeckoResponse"
import type { UseQueryResult } from "@tanstack/react-query"
import type { TokenUsdPriceData } from "../../../hooks/useTokensUsdPrices"
import type { TokenInfo } from "../../../types/base"
import { formatTokenValue } from "../../../utils/format"
import getTokenUsdPrice, {
  getTokenPrice,
} from "../../../utils/getTokenUsdPrice"
import { isBaseToken, isUnifiedToken } from "../../../utils/token"
import { computeTotalBalanceDifferentDecimals } from "../../../utils/tokenUtils"
import type { BalanceMapping } from "../../machines/depositedBalanceMachine"
import type { Holding } from "../types/sharedTypes"

export type TokenPrices = Record<string, number>

export function computeTotalUsdValue(holdings: Holding[]) {
  return holdings.reduce((acc, { usdValue }) => {
    return usdValue == null ? acc : acc + usdValue
  }, 0)
}

/**
 * Determines if a query is in a hard-error state with zero cached data.
 * Keeps stale data visible if a background automatic refetch fails.
 */
export function hasQueryErrorWithoutData(
  query: Pick<UseQueryResult, "dataUpdatedAt" | "errorUpdatedAt">
): boolean {
  return query.dataUpdatedAt === 0 && query.errorUpdatedAt > 0
}

export function combineBalances(
  tokenList: TokenInfo[],
  [
    depositedBalanceQuery,
    transitBalanceQuery,
    tokensUsdPricesQuery,
    tokenPriceChangesQuery,
  ]: [
    UseQueryResult<BalanceMapping, Error>,
    UseQueryResult<BalanceMapping, Error>,
    UseQueryResult<TokenUsdPriceData, Error>,
    UseQueryResult<TokenPriceChangeData, Error>,
  ]
) {
  const hasLoadError = hasQueryErrorWithoutData(depositedBalanceQuery)

  if (depositedBalanceQuery.isPending || depositedBalanceQuery.isError) {
    return {
      data: undefined,
      isPending: depositedBalanceQuery.isPending,
      isError: depositedBalanceQuery.isError,
      hasLoadError,
    }
  }

  const data = tokenList
    .map((token): Holding => {
      const value = computeTotalBalanceDifferentDecimals(
        token,
        depositedBalanceQuery.data ?? {},
        { strict: false }
      )

      const usdValue = value
        ? (getTokenUsdPrice(
            formatTokenValue(value.amount, value.decimals),
            token,
            tokensUsdPricesQuery.data
          ) ?? undefined)
        : undefined
      const usdPrice =
        getTokenPrice(token, tokensUsdPricesQuery.data) ?? undefined
      const usd24hChangePct =
        tokenPriceChangesQuery.data?.[token.symbol]?.usd24hChangePct ??
        undefined

      const transitValue = computeTotalBalanceDifferentDecimals(
        token,
        transitBalanceQuery.data ?? {},
        { strict: false }
      )

      const transitUsdValue = transitValue
        ? (getTokenUsdPrice(
            formatTokenValue(transitValue.amount, transitValue.decimals),
            token,
            tokensUsdPricesQuery.data
          ) ?? undefined)
        : undefined

      return {
        token,
        value,
        usdPrice,
        usdValue,
        usd24hChangePct,
        transitValue,
        transitUsdValue,
      }
    })
    .filter(
      (h) =>
        (h.value && h.value.amount !== 0n) ||
        (h.transitValue && h.transitValue.amount !== 0n)
    )
    .sort((a, b) => {
      const totalValueA = (a.usdValue ?? 0) + (a.transitUsdValue ?? 0)
      const totalValueB = (b.usdValue ?? 0) + (b.transitUsdValue ?? 0)
      return totalValueB - totalValueA
    })

  return {
    data,
    isPending: depositedBalanceQuery.isPending,
    isError: depositedBalanceQuery.isError,
    hasLoadError,
  }
}

export function getPriceForToken(
  token: TokenInfo,
  prices: TokenPrices
): number | undefined {
  if (isBaseToken(token)) {
    return prices[token.defuseAssetId]
  }
  if (isUnifiedToken(token)) {
    for (const grouped of token.groupedTokens) {
      const price = prices[grouped.defuseAssetId]
      if (price != null && price > 0) return price
    }
  }
  return undefined
}

export function getHoldingFromBalances(
  token: TokenInfo,
  balances: BalanceMapping,
  tokenPrices: TokenPrices
): Holding {
  const value = computeTotalBalanceDifferentDecimals(token, balances, {
    strict: false,
  })
  const price = getPriceForToken(token, tokenPrices)
  const usdValue =
    value != null && price != null
      ? Number(formatTokenValue(value.amount, value.decimals)) * price
      : undefined

  return {
    token,
    value,
    usdPrice: price,
    usdValue,
    usd24hChangePct: undefined,
    transitValue: undefined,
    transitUsdValue: undefined,
  }
}

export function getHoldingsFromBalances(
  tokenList: TokenInfo[],
  balances: BalanceMapping,
  tokenPrices: TokenPrices
): Holding[] {
  return tokenList
    .map((token) => getHoldingFromBalances(token, balances, tokenPrices))
    .filter((h) => h.value != null && h.value.amount !== 0n)
    .sort((a, b) => (b.usdValue ?? 0) - (a.usdValue ?? 0))
}
